import { useRef, useMemo, useCallback } from 'react';
import { useParams } from 'react-router-dom';

import noop from 'lodash/noop';
import PlusCircle from '@decisiv/iconix/lib/components/PlusCircle';

import Modal from '@decisiv/ui-components/lib/components/Modal';
import { ButtonProps } from '@decisiv/ui-components/lib/components/Button';
import { useNotifications } from '@decisiv/ui-components';
import { t } from '@lingui/macro';

import { createForm, FormHandler } from 'components/Form';
import { useFormContext } from 'react-hook-form';
import * as yup from 'yup';

import useDebouncedValue from 'hooks/useDebouncedValue';
import { API_SEARCH_DEBOUNCE_DELAY_MS } from 'pages/Management/constants';

import DepotForm from './DepotForm';
import {
  useCreateDepotMutation,
  useManagedPlatformAccountsQuery,
} from './hooks';
import useCreateDepotCapability from './useCreateDepotCapability';

import baseValidationSchema from './validations';

type FormData = yup.InferType<typeof baseValidationSchema>;

const { Form, FormProvider } = createForm<FormData>();

const initialValues: FormData = {
  accountId: '',
  dmsId: '',
  note: '',
  name: '',
  email: '',
  address: '',
  city: '',
  state: '',
  postalCode: '',
  country: 'US',
};

interface ContentProps {
  visible?: boolean;
  onClose: () => void;
}

const Content = ({ visible, onClose }: ContentProps) => {
  const {
    formState: { isValid },
    getValues,
    reset,
    setError,
  } = useFormContext();
  const { notify } = useNotifications();
  const { accountId } = useParams();

  const [, setAccountSearch, debouncedAccountSearch] = useDebouncedValue(
    '',
    API_SEARCH_DEBOUNCE_DELAY_MS,
  );

  const accountsQuery = useManagedPlatformAccountsQuery({
    variables: { page: 1, text: debouncedAccountSearch || undefined },
    skip: !!accountId,
  });

  const { capability: createDepotCapability } = useCreateDepotCapability({
    platformAccountId: accountId,
  });
  const isDenied = createDepotCapability?.allowed === false;

  const formRef = useRef<FormHandler<FormData>>(null);

  /* istanbul ignore next -- @preserve */
  const handleOnSubmit = useCallback(async () => {
    formRef.current?.submit();
  }, []);

  const clearFormAndClose = useCallback(() => {
    reset();
    onClose();
  }, [onClose, reset]);

  const [createDepot, { loading }] = useCreateDepotMutation({
    onCompleted: (data) => {
      /* istanbul ignore next -- @preserve */
      if (data?.createAccountDepot?.depot) {
        notify({
          title: t`Depot created successfully.`,
          intent: 'success',
          duration: 5000,
        } as any);
        clearFormAndClose();
      } else if (data?.createAccountDepot?.errors?.length) {
        let uniquenessErrorHandled = false;
        data.createAccountDepot.errors.forEach((error) => {
          if (error.field === 'dms_id' && error.code === 'taken') {
            setError('dmsId', {
              type: 'manual',
              message: t`Depot IDs must be unique across all teams.`,
            });
            uniquenessErrorHandled = true;
          }
        });
        if (!uniquenessErrorHandled) {
          notify({
            title: t`Depot creation failed due to an unexpected error. Try again or contact support if the issue persists.`,
            intent: 'danger',
            onClose: noop,
          } as any);
          clearFormAndClose();
        }
      }
    },
    onError: (error) => {
      notify({
        title: t`Failed to create depot: ${error.message}`,
        intent: 'danger',
        onClose: noop,
      } as any);
    },
  });

  const handleSubmit = useCallback(async () => {
    const formData = getValues();
    await createDepot({
      variables: {
        platformAccountId:
          accountId ||
          /* istanbul ignore next -- @preserve */ formData.accountId,
        attributes: {
          dmsId: formData.dmsId,
          note: formData.note,
          name: formData.name,
          email: formData.email,
          address1: formData.address,
          city: formData.city,
          state: formData.state,
          postalCode: formData.postalCode,
          country: formData.country,
        },
      },
    });
  }, [getValues, createDepot, accountId]);

  const actions: ButtonProps[] = useMemo(() => {
    return [
      {
        disabled: !isValid || loading || isDenied,
        text: t`Create Depot`,
        loading,
        loadingText: t`Creating...`,
        onClick: handleSubmit,
      },
      { text: t`Cancel`, onClick: clearFormAndClose, disabled: loading },
    ];
  }, [isValid, loading, isDenied, handleSubmit, clearFormAndClose]);

  return (
    <Modal
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore //TODO: [KDS-TYPE-FIX] needs to fix the type of actions and export it
      actions={actions}
      icon={PlusCircle}
      intent="information"
      onClose={clearFormAndClose}
      title={t`Create Depot`}
      visible={visible}
    >
      <Form flex={1} ref={formRef} onSubmit={handleOnSubmit}>
        <DepotForm
          accountId={accountId}
          accountsQuery={accountsQuery}
          capability={createDepotCapability}
          onAccountSearchChange={setAccountSearch}
        />
      </Form>
    </Modal>
  );
};

interface CreateDepotModalProps {
  visible?: boolean;
  handleClose: () => void;
}

export default function CreateDepotModal({
  visible,
  handleClose,
}: CreateDepotModalProps) {
  const { accountId } = useParams();

  const validationSchema = useMemo(() => {
    return baseValidationSchema.shape({
      accountId: accountId
        ? yup.string().optional()
        : /* istanbul ignore next -- @preserve */ yup
            .string()
            .required(t`Account ID is required`),
    });
  }, [accountId]);

  return (
    <FormProvider
      initialValues={initialValues}
      validationSchema={validationSchema}
    >
      <Content onClose={handleClose} visible={visible} />
    </FormProvider>
  );
}
